import { FormControl, InputLabel, ToggleButton, ToggleButtonGroup, Select } from '../primitives/index.js'
import { Typography } from '../primitives/index.js'
import type { SortOptions } from '@tunasong/models'
import type { Entity, EntityType } from '@tunasong/schemas'
import type React from 'react'
import type { ChangeEvent, FC, HTMLAttributes } from 'react'
import invariant from 'tiny-invariant'
import { VBox } from '../layout/vertical-box.js'
import EntityFilter from './entity-filter.js'

export interface SortFilterProps extends SortOptions, Omit<HTMLAttributes<HTMLDivElement>, 'order'> {
  filter?: EntityType[]
  /** If specified, used to determine the filter types */
  entities?: Entity[]
  onSort(spec: SortOptions): void
  onFilter(filter: EntityType[]): void
}

export const SortFilter: FC<SortFilterProps> = props => {
  const { entities, filter, sortBy, order = 'asc', type, elementOrder, onSort, onFilter, style, ...restProps } = props

  const filterTypes = entities ? [...new Set(entities.map(e => e.type))].sort() : undefined

  const handleSortModeChange = (_: React.MouseEvent<HTMLButtonElement>, value: string | string[]) => {
    if (value === 'manual') {
      onSort({ type: 'order', elementOrder })
    } else {
      onSort({ type: 'field', sortBy: sortBy ?? 'name', order, elementOrder })
    }
  }

  const handleSortByChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    onSort({ type: 'field', sortBy: value, order, elementOrder })
  }
  const handleOrderChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    invariant(value === 'asc' || value === 'desc', 'Expected order to be asc or desc: ' + value)
    onSort({ type: 'field', sortBy, order: value as 'asc' | 'desc', elementOrder })
  }

  const isManual = type === 'order'
  const sortMode = isManual ? 'manual' : 'auto'

  return (
    <VBox
      style={{
        width: '100%',
        gap: 16,
        ...style,
      }}
      {...restProps}
    >
      <EntityFilter value={filter} types={filterTypes} onChange={(_, filter) => onFilter(filter)} />

      <ToggleButtonGroup value={sortMode} exclusive onChange={handleSortModeChange} size="small" aria-label="sort mode">
        <ToggleButton value="auto" aria-label="auto sort">
          <Typography variant="caption">Auto</Typography>
        </ToggleButton>
        <ToggleButton value="manual" aria-label="manual sort">
          <Typography variant="caption">Manual</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      {!isManual && (
        <>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              id="sort-by-select"
              value={sortBy ?? 'name'}
              onChange={handleSortByChange}
            >
              <option value="name">Name</option>
              <option value="type">Type</option>
              <option value="updatedAt">Updated</option>
            </Select>
          </FormControl>

          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>Sort Order</InputLabel>
            <Select
              id="sort-order"
              value={order}
              onChange={handleOrderChange}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Select>
          </FormControl>
        </>
      )}
    </VBox>
  )
}

export default SortFilter
